import { Maximize2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

interface ZoomableImageProps {
  src: string
  alt: string
  /** 缩略图限高类, 默认 max-h-72。不要用 aspect-* —— 见下方注释。 */
  thumbClassName?: string
  onError?: () => void
  /** 放大按钮的无障碍标签, 缺省用通用文案。 */
  zoomLabel?: string
  /** 缩略图外层若本身是可点区域(如"设为主图"按钮), 放大必须吞掉事件不触发它。 */
  stopPropagation?: boolean
}

/**
 * 缩略图 + 站内放大。
 *
 * 🔴 两条硬约束, 都是踩出来的:
 * 1. **不用 `aspect-video`**: 分格图常是竖构图、角色设计稿是四视图横向宽图,
 *    固定 16:9 会把它们压成细带或裁掉两侧, 小到看不清就等于用不了。
 *    统一用「限高 + object-contain + 保持原始比例」。
 * 2. **不用 `object-cover`**: 它会**裁掉**画面内容而不只是缩放 ——
 *    四视图被裁掉左右两视图后, 用户根本不知道图是残的。
 *
 * 放大走站内 Dialog 而不是 `<a target="_blank">`: 新标签会离开当前工位,
 * 看完还得切回来, 且丢失所在卡片的上下文。
 */
export function ZoomableImage({
  src,
  alt,
  thumbClassName = 'max-h-72',
  onError,
  zoomLabel,
  stopPropagation = false,
}: ZoomableImageProps) {
  const { t } = useTranslation()
  const [zoomed, setZoomed] = useState(false)
  const label = zoomLabel ?? t('common.zoom')
  const open = (event: { preventDefault: () => void; stopPropagation: () => void }) => {
    event.preventDefault()
    if (stopPropagation) event.stopPropagation()
    setZoomed(true)
  }
  return (
    <>
      <span className="relative block">
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className={cn('w-full bg-muted object-contain', thumbClassName)}
          onError={onError}
        />
        <span
          role="button"
          tabIndex={0}
          aria-label={label}
          className="absolute right-1 top-1 inline-flex size-7 items-center justify-center rounded-md border bg-background/85 hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={open}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') open(event)
          }}
        >
          <Maximize2 className="size-3.5" />
        </span>
      </span>
      <Dialog open={zoomed} onOpenChange={setZoomed}>
        <DialogContent className="max-w-5xl">
          <DialogTitle className="sr-only">{alt}</DialogTitle>
          <img src={src} alt={alt} className="max-h-[80vh] w-full object-contain" />
        </DialogContent>
      </Dialog>
    </>
  )
}
